import React, { useState, useContext, useEffect } from 'react';
import { TableContext } from '../context';
import axios from 'axios';
import { Accordion, AccordionSummary, AccordionDetails, Button, MenuItem, Select, InputAdornment, IconButton, Checkbox, TextField, FormControlLabel, Popover, Typography, Badge } from "@mui/material";
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import FilterListIcon from '@mui/icons-material/FilterList';
import ClearIcon from '@mui/icons-material/Clear';
import './Subscriptions.css';
import "../App.css";
import { toast } from "react-toastify";
import Loader from './Loader';

const NotificationSettings = () => {
    const { subscriptions, subscriptionsLoading, setSubscriptionsLoading, graphData, getSubscriptions,getToken } = useContext(TableContext);
    const [searchQuery, setSearchQuery] = useState("");
    const [anchorEl, setAnchorEl] = useState(null);
    const [expanded, setExpanded] = useState(false);
    const [settingsLoading, setSettingsLoading] = useState(true);
    const [settings, setSettings] = useState(
        subscriptions.reduce((acc, app) => {
            acc[app] = {
            applicationID: 0,
            upstreamSubscribed: false,
            downstreamSubscribed: false,
            notificationType: "false",
            changed: false,
            };
            return acc;
        }, {})
    );

    const handleCheckboxChange = (app, field) => {
        setSettings((prev) => ({
          ...prev,
          [app]: { ...prev[app], [field]: !prev[app][field], changed: true },
        }));
    };

    const handleNotificationChange = (app) => {
        setSettings((prev) => ({
          ...prev,
          [app]: { 
            ...prev[app], 
            notificationType: prev[app].notificationType === "false" ? "instant" : "false", 
            changed: true 
          },
        }));
    };
    
    const handleDropdownChange = (app, event) => {
        setSettings((prev) => ({
          ...prev,
          [app]: { ...prev[app], notificationType: event.target.value, changed: true },
        }));
    };

    const handleSubscribedStatesChange = (app, state) => {
        setSettings((prev) => {
            const subscribedStates = prev[app].subscribedStates?.includes(state)
                ? prev[app].subscribedStates.filter((s) => s !== state)
                : [...prev[app].subscribedStates, state];
            return {
                ...prev,
                [app]: { ...prev[app], subscribedStates, changed: true },
            };
        });
    };
    
    const handleSave = async (app) => {
        console.log("Saved settings for:", app, settings[app]);
        try {
            const response = await axios.post(
                `${process.env.REACT_APP_BACKEND_URI}/api/subscription/updateSubscription`,
                {
                    user_email: graphData?.mail,
                    app_name: app,
                    upstream_subscribed: settings[app].upstreamSubscribed,
                    downstream_subscribed: settings[app].downstreamSubscribed,
                    notification_type: settings[app].notificationType,
                    subscribed_states: settings[app].subscribedStates,
                },
                {
                    headers: {
                        Authorization: "Bearer " + localStorage.getItem("jwt_token"),
                    },
                }
            );
            
            if (response.data.status === 200 && response.data.message === "Subscription updated successfully") {
                console.log("Updated Settings: ", response.data.updated_subscription);
                toast.success(`Notification settings updated successfully for ${app}`, {
                    position: "top-center",
                    autoClose: 3000,
                    hideProgressBar: false,
                    closeOnClick: true,
                    pauseOnHover: true,
                    draggable: true,
                    progress: undefined,
                    theme: "light",
                });
                setSettings((prev) => ({
                    ...prev,
                    [app]: { ...prev[app], changed: false },
                }));
            } else {
                console.error('Failed to update notification settings');
                toast.error("Failed to update notification settings", {
                    position: "top-center",
                    autoClose: 3000,
                    hideProgressBar: false,
                    closeOnClick: true,
                    pauseOnHover: true,
                    draggable: true,
                    progress: undefined,
                    theme: "light",
                });
                setExpanded(false);
            }
        } catch (error) {
        console.error(error);
        toast.error("Failed to update notification settings", {
            position: "top-center",
            autoClose: 3000,
            hideProgressBar: false,
            closeOnClick: true,
            pauseOnHover: true,
            draggable: true,
            progress: undefined,
            theme: "light",
        });
        setExpanded(false);
        }
    };

    const getNotificationSettings = async (applicationName) => {
        try {
            const response = await axios.post(
                `${process.env.REACT_APP_BACKEND_URI}/api/subscription/getSubscriptionSettings`,
                {
                    user_email: graphData?.mail,
                    app_name: applicationName,
                },
                {
                    headers: {
                        Authorization: "Bearer " + localStorage.getItem("jwt_token"),
                    },
                }
            );
            
            if (response.data.status === 200) {
                console.log("Settings: ", response.data.settings);
                return new Promise((resolve) => {
                    setSettings((prevSettings) => {
                        const newSettings = {
                            ...prevSettings,
                            [applicationName]: response.data.settings
                        };
                        resolve();
                        return newSettings;
                    });
                });
            } else {
                console.error('Failed to fetch notification settings');
                toast.error("Failed to fetch Notification settings", {
                    position: "top-center",
                    autoClose: 3000,
                    hideProgressBar: false,
                    closeOnClick: true,
                    pauseOnHover: true,
                    draggable: true,
                    progress: undefined,
                    theme: "light",
                });
                setExpanded(false);
            }
        } catch (error) {
        console.error(error);
        toast.error("Failed to fetch Notification settings", {
            position: "top-center",
            autoClose: 3000,
            hideProgressBar: false,
            closeOnClick: true,
            pauseOnHover: true,
            draggable: true,
            progress: undefined,
            theme: "light",
        });
        setExpanded(false);
        }       
    };

    const handleUnsubscribeClick = async (applicationName) => {
        try {
            const response = await axios.post(
                `${process.env.REACT_APP_BACKEND_URI}/api/subscription/unsubscribe`,
                {
                    user_email: graphData?.mail,
                    app_name: [applicationName],
                },
                {
                    headers: {
                        Authorization: "Bearer " + localStorage.getItem("jwt_token"),
                    },
                }
            );
            
            if (response.data.status === 200) {
                setExpanded(false);
                setSubscriptionsLoading(true);
                getSubscriptions();
                toast.success(`${applicationName} Unsubscribed successfully`, {
                    position: "top-center",
                    autoClose: 3000,
                    hideProgressBar: false,
                    closeOnClick: true,
                    pauseOnHover: true,
                    draggable: true,
                    progress: undefined,
                    theme: "light",
                });
            } else {
                console.error('Failed to Unsubscribe to Application');
                toast.error("Failed to Unsubscribe to Application", {
                    position: "top-center",
                    autoClose: 3000,
                    hideProgressBar: false,
                    closeOnClick: true,
                    pauseOnHover: true,
                    draggable: true,
                    progress: undefined,
                    theme: "light",
                });
            }
        } catch (error) {
            console.error(error);
            toast.error("Failed to Unsubscribe to Application(s)", {
                position: "top-center",
                autoClose: 3000,
                hideProgressBar: false,
                closeOnClick: true,
                pauseOnHover: true,
                draggable: true,
            progress: undefined,
            theme: "light",
        });
        }
    };

    const handleSearchChange = (event) => {
        setSearchQuery(event.target.value);
    };

    const handleFilterClick = (event) => {
        setAnchorEl(event.currentTarget);
    };

    const handleFilterClose = () => {
        setAnchorEl(null);
    };

    const open = Boolean(anchorEl);
    const id = open ? 'simple-popover' : undefined;

    const filteredSubscriptions = subscriptions.filter((app) =>
        app.toLowerCase().includes(searchQuery.trim().toLowerCase())
    );

    const handleAccordionChange = (index, app) => async (event, isExpanded) => {
        console.log("Inside function handleAccordionChange");
        setExpanded(isExpanded ? index : false);
        setSettingsLoading(true);
        if (isExpanded) {
            await getNotificationSettings(app);
            setSettingsLoading(false);
        }
    };

    return(
        <div>
            <h2>Notification Settings</h2>
            
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", margin: "10px"}}>
                <Badge badgeContent={searchQuery ? 1 : 0} color="primary">
                    <Button style={{color: "#42b0d5"}} onClick={handleFilterClick}>
                        <FilterListIcon />
                        <Typography variant="caption" style={{ marginLeft: "5px" }}>FILTER</Typography>
                    </Button>
                </Badge>
                <Popover
                    id={id}
                    open={open}
                    anchorEl={anchorEl}
                    onClose={handleFilterClose}
                    anchorOrigin={{
                        vertical: 'bottom',
                        horizontal: 'left',
                    }}
                >
                    <div style={{ padding: "10px", display: "flex", flexDirection: "column", position: "relative" }}>
                        {/* <IconButton
                            style={{ position: "absolute", top: "5px", right: "5px" }}
                            onClick={handleFilterClose}
                        >
                            <CloseIcon fontSize="small" />
                        </IconButton> */}
                        <div style={{ display: "flex", justifyContent: "space-between" }}>
                            <Typography variant="subtitle1" style={{ width: "45%" }}>Filter By</Typography>
                            <Typography variant="subtitle1" style={{ width: "45%" }}>Value</Typography>
                        </div>
                        <div style={{ display: "flex", justifyContent: "space-between", marginTop: "5px", overflow: "hidden", marginBottom: "5px" }}>
                            <Select value="Application" style={{ height: "30px", width: "45%" }}>
                                <MenuItem value="Application">Application</MenuItem>
                            </Select>
                            <TextField
                                variant="outlined"
                                placeholder="Filter Value"
                                value={searchQuery}
                                onChange={handleSearchChange}
                                style={{ width: "45%" }}
                                InputProps={{
                                    style: {
                                        height: "30px",
                                        padding: '0px'
                                    },
                                    endAdornment: searchQuery && (
                                        <InputAdornment position="end">
                                            <IconButton onClick={() => setSearchQuery('')}>
                                                <ClearIcon fontSize="small"/>
                                            </IconButton>
                                        </InputAdornment>
                                    )
                                }}
                            />
                        </div>
                    </div>
                </Popover>
                <strong>{subscriptions.length} Applications Subscribed</strong>  
            </div>
            {subscriptionsLoading ? ( 
                <Loader />
            ):(
                <div className="table-container">
                    {/* Subscriptions List */}
                    <div className="notifications-list" style={{marginBottom: "10vh"}}>
                        {filteredSubscriptions.length > 0 ? (
                        filteredSubscriptions.map((app, index) => (
                        <div className="notification-item" key={index}>
                            <Accordion 
                                expanded={expanded === index} 
                                onChange={handleAccordionChange(index, app)} 
                                style={{ boxShadow: "none", backgroundColor: "transparent" }}
                            >
                            <AccordionSummary expandIcon={<ExpandMoreIcon />} style={{ display: "flex", alignItems: "center" }}>
                                <strong>{app}</strong>
                            </AccordionSummary>
                            {!settingsLoading ? (
                                <AccordionDetails>
                                    <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", width: "100%" }}>
                                        <div style={{ flex: 0.5, margin: '10px' }}>
                                            <strong>App ID:</strong> {settings[app]?.applicationID}
                                        </div>
                                        <div style={{ flex: 1, margin: '10px' }}>
                                            <strong>Upstream/Downstream Applications:</strong>
                                            <div style={{ display: "flex", flexDirection: "column", marginTop: "5px" }}>
                                                <FormControlLabel
                                                    control={
                                                        <Checkbox
                                                            checked={settings[app]?.upstreamSubscribed}
                                                            onChange={() => handleCheckboxChange(app, "upstreamSubscribed")}
                                                        />
                                                    }
                                                    label="Subscribe to Upstream Applications"
                                                />
                                                <FormControlLabel
                                                    control={
                                                        <Checkbox
                                                            checked={settings[app]?.downstreamSubscribed}
                                                            onChange={() => handleCheckboxChange(app, "downstreamSubscribed")}
                                                        />
                                                    }
                                                    label="Subscribe to Downstream Applications"
                                                />
                                            </div>
                                        </div>
                                        <div style={{ flex: 0.7, margin: '10px' }}>
                                            <strong>Subscribed States:</strong>
                                            <div className="custom-scrollbar" style={{ display: "flex", flexDirection: "column", maxHeight: "100px", overflowY: "auto", marginTop: "5px" }}>
                                                {["New", "Assess", "Authorize", "Scheduled", "Implement", "Review", "Closed", "Canceled"].map((state) => (
                                                    <FormControlLabel
                                                        key={state}
                                                        control={
                                                            <Checkbox
                                                                checked={settings[app]?.subscribedStates?.includes(state)}
                                                                onChange={() => handleSubscribedStatesChange(app, state)}
                                                            />
                                                        }
                                                        label={state}
                                                    />
                                                ))}
                                            </div>
                                        </div>
                                        <div style={{ flex: 1, display: "flex", flexDirection: "column", margin: '10px' }}>
                                            <strong>Notifications:</strong>
                                            <div style={{ marginTop: "5px" }}>
                                                <FormControlLabel
                                                    control={
                                                        <Checkbox
                                                            checked={settings[app]?.notificationType !== "false"}
                                                            onChange={() => handleNotificationChange(app)}
                                                        />
                                                    }
                                                    label="Receive Notifications via Email"
                                                />
                                            </div>
                                        </div>
                                        {settings[app]?.notificationType !== "false" && (
                                            <div style={{ flex: 1, margin: '10px' }}>
                                                <strong>Email Notifications Frequency:</strong>
                                                <Select
                                                    value={settings[app]?.notificationType}
                                                    onChange={(e) => handleDropdownChange(app, e)}
                                                    
                                                    style={{ width: "200px", height: "40px" }}
                                                >
                                                    <MenuItem value="instant">Instant</MenuItem>
                                                    <MenuItem value="twiceDaily">Twice Daily</MenuItem>
                                                    <MenuItem value="Daily">Daily</MenuItem>
                                                    <MenuItem value="Weekly">Weekly (Every Friday)</MenuItem>
                                                    <MenuItem value="Biweekly">Biweekly (Wednesday & Friday)</MenuItem>
                                                </Select>
                                            </div>
                                        )}
                                        <div style={{ flex: 1, display: "flex", flexDirection: "column", alignItems: "center" }}>
                                            <Button 
                                                variant="contained" 
                                                style={{ marginBottom: "10px", backgroundColor: "#FA6A55" }}
                                                onClick={() => toast.promise(handleUnsubscribeClick(app), {
                                                    pending: "Unsubscribing...",
                                                })}
                                            >
                                                Unsubscribe
                                            </Button>
                                            <Button
                                                variant="contained"
                                                color="primary"
                                                disabled={!settings[app]?.changed}
                                                onClick={() => toast.promise(handleSave(app), {
                                                    pending: "Updating Notification Settings",
                                                })}
                                            >
                                                Save Changes
                                            </Button>
                                        </div>
                                    </div>
                                </AccordionDetails>
                            ): (
                                <AccordionDetails>
                                    <div style={{ display: "flex", alignItems: "center", justifyContent: "center", width: "100%" }}>
                                        <div style={{ textAlign: 'center', margin: '20px' }}>
                                            <div className="loader"></div>
                                            {/* <p>Loading...</p> */}
                                        </div>
                                    </div>
                                </AccordionDetails>
                            )}
                            </Accordion>
                        </div>
                        )))
                        :
                        (<div style={{ textAlign: 'center', margin: '20px' }}>
                            <p>No matching subscriptions available.</p>
                        </div>)}
                    </div>
                </div>
            )}
            

        </div>
    );
}

export default NotificationSettings;